/**
 * useFileText — fetch a case file as text for a preview. `limit` cuts the text (with "…") when
 * `cut` is true; with `cut` false a longer file reports `tooLarge` and no text, so a document is never
 * rendered half-parsed. Aborts on change or unmount; a stale response never shows for another URL.
 */

import { useEffect, useState } from 'react';

const EMPTY = { url: null, text: null, error: null, tooLarge: false };

export default function useFileText(url, enabled, { limit = 65536, cut = true } = {}) {
  const [state, setState] = useState(EMPTY);
  useEffect(() => {
    if (!enabled || !url) { setState(EMPTY); return undefined; }
    const controller = new AbortController();
    setState({ ...EMPTY, url });
    fetch(url, { credentials: 'include', signal: controller.signal })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((text) => {
        if (text.length <= limit) setState({ ...EMPTY, url, text });
        else if (cut) setState({ ...EMPTY, url, text: `${text.slice(0, limit)}\n…` });
        else setState({ ...EMPTY, url, tooLarge: true });
      })
      .catch((err) => { if (!controller.signal.aborted) setState({ ...EMPTY, url, error: err.message }); });
    return () => controller.abort();
  }, [url, enabled, limit, cut]);
  return state.url === url ? state : { ...EMPTY, url };
}
